/**
 * Shared formatters for Telegram (and push) notification bodies.
 * Keeps layout consistent across polls, tournaments, challenges, bookings, etc.
 */

/**
 * Returns a short timezone abbreviation like "IST", "CET", "EST" for a given date+tz.
 * Falls back to GMT/UTC offset when no abbreviation is available.
 */
export function getTimezoneAbbr(date: Date, timezone?: string | null): string {
  const tz = timezone || undefined;
  const short = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'short',
    ...(tz ? { timeZone: tz } : {}),
  })
    .formatToParts(date)
    .find((p) => p.type === 'timeZoneName')?.value ?? '';
  if (short && !/^(GMT|UTC)/.test(short)) return short;

  const long = new Intl.DateTimeFormat('en-US', {
    timeZoneName: 'long',
    ...(tz ? { timeZone: tz } : {}),
  })
    .formatToParts(date)
    .find((p) => p.type === 'timeZoneName')?.value ?? '';
  if (long && !long.startsWith('GMT') && !long.startsWith('UTC')) {
    const abbr = long.split(' ').map((w) => w[0]).join('').toUpperCase();
    if (abbr.length >= 2 && abbr.length <= 5) return abbr;
  }
  return short;
}

/**
 * "Sat, 25 Apr · 18:00 IST" — timezone-aware date+time formatter for notifications.
 */
export function formatDateTime(iso: string | Date, timezone?: string | null): string {
  const date = iso instanceof Date ? iso : new Date(iso);
  if (isNaN(date.getTime())) return '';
  const tz = timezone || undefined;

  const datePart = new Intl.DateTimeFormat('en-US', {
    weekday: 'short',
    day: 'numeric',
    month: 'short',
    ...(tz ? { timeZone: tz } : {}),
  }).format(date);

  const timePart = new Intl.DateTimeFormat('en-US', {
    hour: '2-digit',
    minute: '2-digit',
    hour12: false,
    ...(tz ? { timeZone: tz } : {}),
  }).format(date);

  const tzAbbr = getTimezoneAbbr(date, timezone);
  return `${datePart} · ${timePart}${tzAbbr ? ' ' + tzAbbr : ''}`;
}

/**
 * "Padel Club Madrid (Salamanca)" | "Padel Club Madrid" | "TBD"
 */
export function formatLocation(club?: { club_name?: string | null; city?: string | null } | null): string {
  if (!club || !club.club_name) return 'TBD';
  if (club.city) return `${club.club_name} (${club.city})`;
  return club.club_name;
}

/**
 * Build a label-aligned multi-line block for Telegram messages.
 * Labels are padded so values line up visually in monospace previews.
 *
 * Example:
 *   buildSections('🏆 New Tournament · Spring Cup', [
 *     ['Format', 'Mexicano'],
 *     ['When', 'Sat, 25 Apr · 09:00 IST'],
 *     ['Where', 'Padel Club Madrid'],
 *   ], 'Sign up in the app.')
 */
export function buildSections(
  headerLine: string,
  rows: Array<[string, string | undefined | null]>,
  footer?: string
): string {
  const filtered = rows.filter((r): r is [string, string] => !!r[1]);
  const maxLabelLen = filtered.reduce((m, [label]) => Math.max(m, label.length), 0);
  const body = filtered
    .map(([label, value]) => `${(label + ':').padEnd(maxLabelLen + 2)} ${value}`)
    .join('\n');
  const parts = [headerLine];
  if (body) parts.push(body);
  if (footer) parts.push(footer);
  return parts.join('\n');
}
